/* hh-qb-engine.js — Batch #8. One engine, two datasets, two modes.

   dataset: 'pumpkins' | 'trees'      mode: 'public' | 'internal'
   Read from data attributes on the .hh-qb wrapper; the data file is fetched
   from the URL in data-hh-qb-src.

   PUBLIC MODE SHIPS NO PRICES. The public JSON has none, so this file cannot
   render one even by accident — there is no price field to read. Internal mode
   fetches the -priced file and is served only from the staff handle.

   Tier ladder (Batch #8 §0.3): base · 12% off at 25+ · 25% off at 50+, counted
   per tier_group. Garland has its own ladder, by roll: 2 rolls 12%, 3+ 25%.
   Minimum: trees 10 items across all sections (one roll = one item);
   pumpkins 2,000 lb of estimated shipping weight. */
(function () {
  'use strict';

  var NOUN = {trees: 'trees', wreaths: 'wreaths', supplies: 'stands', garland: 'garland'};

  function nounFor(group, cat) {
    return NOUN[group] || (cat || 'items').toLowerCase();
  }
  function lb(v) { return Math.round(v / 10) * 10; }
  /* Batch #10B §D — a weight that cannot be computed renders as an em dash.
     Never `NaN lb` (the regression this replaces) and never `0 lb`, which would
     read as a real measurement of nothing. */
  function fmtLb(v) {
    if (v == null || !isFinite(v)) return '—';
    return lb(v).toLocaleString('en-US') + ' lb';
  }
  function money(v) {
    return '$' + v.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }
  /* Batch #10 §1.4 — the email wants a human date. Built from the ISO string by
     hand: new Date('2026-11-20') parses as UTC midnight, so every US timezone
     formats it as the 19th. No Date object touches this value anywhere. */
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
                'August', 'September', 'October', 'November', 'December'];
  function prettyDate(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    return m ? MONTHS[+m[2] - 1] + ' ' + (+m[3]) + ', ' + m[1] : (iso || '');
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function init(root) {
    if (root.getAttribute('data-hh-qb-ready')) return;
    root.setAttribute('data-hh-qb-ready', '1');

    /* Batch #10B §B.1 — the ordering window is closed for this dataset. Liquid
       rendered the notice instead of the form, so there is no product list to
       fill and no submit to wire. Bail before the fetch: without this the
       dataset request still fires and build() dereferences controls that are
       not in the DOM. */
    if (root.getAttribute('data-hh-qb-closed') === '1') return;

    var mode = root.getAttribute('data-hh-qb-mode') || 'public';
    var ctx = root.getAttribute('data-hh-qb-ctx') || 'wholesale';
    var src = root.getAttribute('data-hh-qb-src');
    var endpoint = root.getAttribute('data-hh-qb-endpoint') || 'https://formspree.io/f/xdenbapk';
    var fallbackEmail = root.getAttribute('data-hh-qb-email') || 'info@holidayandharvest.com';

    fetch(src, {credentials: 'omit'})
      .then(function (r) { if (!r.ok) throw new Error('bad status'); return r.json(); })
      .then(function (D) { build(root, D, mode, ctx, endpoint, fallbackEmail); })
      .catch(function () {
        var box = root.querySelector('[data-hh-qb-products]');
        if (box) box.textContent = 'Could not load the product list. Please reload, or email us and we will quote it by hand.';
      });
  }

  function build(root, D, mode, ctx, endpoint, fallbackEmail) {
    var P = D.products;
    var qty = P.map(function () { return 0; });   // individuals, or rolls for garland
    var bins = P.map(function () { return 0; });

    /* ---- resubmission -------------------------------------------------
       The automatic estimate email links back here carrying ?quote= and an
       ?o= basket. The quote id is REUSED rather than minted, so the ledger
       recognises the same order, voids the old draft and replaces the row
       instead of opening a second one. ?o= is slug:bins:each, comma
       separated, matched on the product name so it survives a reorder. */
    var incomingQuote = '', prefilled = 0;
    try {
      var QP = new URLSearchParams(window.location.search);
      incomingQuote = (QP.get('quote') || '').trim().slice(0, 24);
      var enc = (QP.get('o') || '').trim();
      if (enc) {
        var slug = function (t) { return String(t).toLowerCase().replace(/[^a-z0-9]+/g, ''); };
        var bySlug = {};
        P.forEach(function (p, i) { bySlug[slug(p.name)] = i; });
        enc.split(',').forEach(function (part) {
          var bit = part.split(':'), i = bySlug[slug(bit[0])];
          if (i === undefined) { return; }
          var nb = parseInt(bit[1], 10) || 0, ne = parseInt(bit[2], 10) || 0;
          if (nb > 0) { bins[i] = nb; prefilled++; }
          if (ne > 0) { qty[i] = ne; prefilled++; }
        });
      }
    } catch (e) { incomingQuote = ''; }
    /* Batch #10 §2.2 — count-on-quote items have no quantity at all, only an
       in-or-out. They are kept OUT of qty[]/bins[] so that every existing sum —
       items, units, weight, tier counts — excludes them without a single one of
       those call sites having to know they exist. */
    var sel = P.map(function () { return false; });
    var sent = false;
    /* Oct 22 — public tree builder: $1,200 minimum, auto-invoiced, never quoted. */
    var TREEPUB = !!(D.minimum && D.minimum.type === 'dollars' && mode !== 'internal');
    var dateTried = false;                   /* §1.1: the "required" message waits
                                                for a submit attempt; the
                                                out-of-window one does not */
    var $ = function (n) { return root.querySelector('[data-hh-qb-' + n + ']'); };

    /* ---- tier ladders ------------------------------------------------- */
    /* Two tier models.
       PERCENT (trees, greens, supplies): a shared ladder of [threshold, saving],
       and the saving is shown to the buyer.
       BREAK (pumpkins): one threshold per section and NO percentage — the volume
       price is set per product upstream (Big Mac discounts 0%, minis 30%), so a
       shown percentage would leak the base-to-volume ratio. Copy is
       "25+ unlocks volume pricing", exactly as Batch #7 shipped it. */
    var MODEL = D.tier_breaks ? 'break' : 'percent';
    var LADDER = {};                       // tier_group -> [[threshold, saving|null], ...]
    if (MODEL === 'percent') {
      var std = Object.keys(D.tier_thresholds)
        .map(function (k) { return [parseInt(k, 10), D.tier_thresholds[k]]; })
        .sort(function (a, b) { return a[0] - b[0]; });
      P.forEach(function (p) { LADDER[p.tier_group] = std; });
      if (D.tier_ladders) {
        Object.keys(D.tier_ladders).forEach(function (g) {
          LADDER[g] = Object.keys(D.tier_ladders[g])
            .map(function (k) { return [parseInt(k, 10), D.tier_ladders[g][k]]; })
            .sort(function (a, b) { return a[0] - b[0]; });
        });
      }
    } else {
      P.forEach(function (p) {
        var br = D.tier_breaks[p.tier_group];
        LADDER[p.tier_group] = br ? [[br, null]] : [];
      });
    }

    /* one quantity drives everything: individuals + bins × per_bin */
    function total(i) {
      var p = P[i];
      return qty[i] + (p.bin_orderable ? bins[i] * p.per_bin : 0);
    }
    function groupCount(g) {
      return P.reduce(function (a, p, i) { return a + (p.tier_group === g ? total(i) : 0); }, 0);
    }
    function ladderTier(g) {
      var n = groupCount(g), hit = null;
      (LADDER[g] || []).forEach(function (t) { if (n >= t[0]) hit = t; });
      return hit;                          // [threshold, saving] or null
    }
    /* ---- Oct 23, 2026 — DJ: trees by the foot ---------------------------
       By-foot trees are 25% off the lot price, 30% when the order is over
       $2,000 ("over" = product subtotal with by-foot trees at 25%, greens /
       garland / stands at their own count tier, the tabletop flat). This is
       treePrice_() in HH_TreeInvoices.gs line for line — the script holds
       any order whose tier line disagrees with its own math. The tabletop is
       priced flat whenever a by-foot tree is in the order; alone, it keeps
       the count ladder on the trees group, as before. */
    var TP = D.tree_pricing || null;
    function priceBase(i) {
      var p = P[i];
      if (mode === 'internal' || p.mp == null) return p.base;
      return Math.round(p.mp * D.minimum.value * 100) / 100;
    }
    function retailMode() {
      if (!TP) return false;
      for (var i = 0; i < P.length; i++) if (P[i].by_foot && total(i) > 0) return true;
      return false;
    }
    function lineN(i) { var p = P[i]; return (p.unit === 'roll' || p.step_ft) ? qty[i] : total(i); }
    function treeAt25() {
      var sum = 0;
      P.forEach(function (p, i) {
        var n = lineN(i); if (!n || p.count_on_quote) return;
        var b = priceBase(i); if (b == null) return;
        var cur = ladderTier(p.tier_group);
        var pct0 = p.by_foot ? TP.off : (p.tier_group === 'trees' ? 0 : ((cur && cur[1] != null) ? cur[1] : 0));
        sum += Math.round(Math.round(b * (1 - pct0) * 100) / 100 * n * 100) / 100;
      });
      return Math.round(sum * 100) / 100;
    }
    /* Oct 23 (rev2) — DJ: the extra 5% applies only to the tree dollars OVER
       $2,000, so adding a tree can never lower the total. Trees stay at 25%;
       the tree dollars past the line (valued at 25%) are repriced to 30%:
       extra = min(at25 - 2000, tree dollars at 25%) x (0.30 - 0.25) / 0.75.
       Same formula as treeExtra_() in HH_TreeInvoices.gs. */
    function treeRetailPct() { return TP.off; }
    function treeExtra() {
      if (!TP || !retailMode()) return 0;
      var a = treeAt25(), over = a - TP.big_over;
      if (over <= 0) return 0;
      var t25 = 0;
      P.forEach(function (p, i) {
        if (!p.by_foot) return;
        var n = lineN(i); if (!n) return;
        t25 += Math.round(Math.round(priceBase(i) * (1 - TP.off) * 100) / 100 * n * 100) / 100;
      });
      var el = Math.min(over, Math.round(t25 * 100) / 100);
      return Math.round(el * (TP.off_big - TP.off) / (1 - TP.off) * 100) / 100;
    }
    function tierOf(g) {
      if (g === 'trees' && retailMode()) return [0, treeRetailPct()];
      return ladderTier(g);
    }
    function nextTier(g) {
      if (g === 'trees' && retailMode()) return null;
      var n = groupCount(g), nx = null;
      (LADDER[g] || []).forEach(function (t) { if (n < t[0] && !nx) nx = t; });
      return nx;
    }

    /* ---- section + card DOM, built once -------------------------------- */
    var wrap = $('products');
    wrap.textContent = '';
    var lastCat = null, catNodes = {};
    /* Oct 22, 2026 — DJ: group the tree list by type. DISPLAY order only: the
       dataset order (P) is untouched, cards keep data-i = their dataset index,
       and the order text, the "Tier reached by section" block and the payload
       all still walk P in dataset order — the auto-invoice parser reads those. */
    var ORDER = P.map(function (p, i) { return i; }), SUB = {};
    if (D.label === 'trees') {
      var rk = function (o, k) { return Object.prototype.hasOwnProperty.call(o, k) ? o[k] : 9; };
      var catRank = {'Christmas Trees': 0, 'Christmas Greens': 1, 'Supplies': 2};
      var subRank = {'Noble Fir': 0, 'Nordmann Fir': 1, 'Douglas Fir': 2, 'Other trees': 3,
                     'Fresh wreaths': 4, 'Cedar garland': 5, 'Tree stands': 6};
      var subOf = function (p) {
        if (p.tier_group === 'trees') {
          if (/Noble/.test(p.name)) return 'Noble Fir';
          if (/Nordmann/.test(p.name)) return 'Nordmann Fir';
          if (/Douglas/.test(p.name)) return 'Douglas Fir';
          return 'Other trees';
        }
        if (p.tier_group === 'wreaths') return 'Fresh wreaths';
        if (p.tier_group === 'garland') return 'Cedar garland';
        if (p.tier_group === 'supplies') return 'Tree stands';
        return '';
      };
      ORDER.sort(function (a, b) {
        return (rk(catRank, P[a].cat) - rk(catRank, P[b].cat))
            || (rk(subRank, subOf(P[a])) - rk(subRank, subOf(P[b])))
            || (a - b);
      });
      ORDER.forEach(function (i) { SUB[i] = subOf(P[i]); });
    }
    var lastSub = null;
    ORDER.forEach(function (i) {
      var p = P[i];
      if (p.cat !== lastCat) {
        lastSub = null;
        var head = el('div', 'hh-qb__cat');
        head.appendChild(el('div', 'hh-qb__cat-name', p.cat));
        var meter = el('div', 'hh-qb__meter'); meter.appendChild(el('i'));
        head.appendChild(meter);
        head.appendChild(el('div', 'hh-qb__cat-note'));
        wrap.appendChild(head);
        catNodes[p.cat] = head;
        lastCat = p.cat;
      }
      if (SUB[i] && SUB[i] !== lastSub) {
        wrap.appendChild(el('div', 'hh-qb__sub', SUB[i]));
        lastSub = SUB[i];
      }
      var card = el('div', 'hh-qb__prod');
      card.setAttribute('data-i', i);
      card.appendChild(el('div', 'hh-qb__pname', p.name));
      card.appendChild(el('div', 'hh-qb__pdesc', p.desc));
      /* §1.2b card order: name · desc · "About N per bin" · tier line ·
         Individuals stepper · Bins stepper. */
      if (p.bin_orderable) {
        card.appendChild(el('div', 'hh-qb__pbin', 'About ' + p.per_bin.toLocaleString('en-US') + ' per bin'));
      }
      var tier = el('div', 'hh-qb__ptier');
      tier.setAttribute('data-tier', i);
      card.appendChild(tier);
      if (mode === 'internal') {
        var pr = el('div', 'hh-qb__price'); pr.setAttribute('data-price', i); card.appendChild(pr);
      }
      /* §2: garland is sold by the foot on every public surface. The stepper
         still steps one roll at a time — that is the freight unit — but the
         label, the value and the tier note are all feet. "roll" never renders. */
      if (p.count_on_quote) {
        card.appendChild(coqRow(p, i));
      } else {
        /* bin_only: a mixed bin has no single unit. H&H does not control which
           varieties come out of the pick, so "one Decorative Mix" is not a thing
           that can be picked, and offering the stepper would take an order that
           cannot be filled. The Bins stepper is the only input. */
        if (!p.bin_only) {
          card.appendChild(stepperRow(p, i, 'unit',
            p.step_ft ? 'Quantity in feet'
                      : (p.unit === 'roll' ? 'Rolls' : (p.cat === 'Hay' ? 'Bales' : 'Individuals'))));
        }
        if (p.bin_orderable) card.appendChild(stepperRow(p, i, 'bin', 'Bins'));
      }
      wrap.appendChild(card);
    });

    /* §2.2 — "Render them selectable with the quantity control replaced by a
       'count on quote' label. No zero, no blank, no invented estimate." So the
       row keeps its shape but the stepper is gone: nothing on this card can
       display a number the price book does not have. The switch is the same one
       the delivery panel uses, so it needs no new styling vocabulary. */
    function coqRow(p, i) {
      var row = el('div', 'hh-qb__row hh-qb__row--coq');
      var lab = el('div', 'hh-qb__rowlab', 'Quantity');
      lab.appendChild(el('b', 'hh-qb__coq', 'count on quote'));
      var sw = el('span', 'hh-qb__switch');
      var cb = el('input');
      cb.type = 'checkbox';
      cb.id = (root.id || 'qb') + '-coq-' + i;
      cb.setAttribute('data-coq', i);
      var knob = el('span', 'hh-qb__knob');
      knob.setAttribute('aria-hidden', 'true');
      var sr = el('label', 'hh-qb__sr', 'Include ' + p.name + ' in my quote');
      sr.setAttribute('for', cb.id);
      sw.appendChild(cb); sw.appendChild(knob); sw.appendChild(sr);
      row.appendChild(lab); row.appendChild(sw);
      return row;
    }

    function stepperRow(p, i, kind, label) {
      var row = el('div', 'hh-qb__row');
      var lab = el('div', 'hh-qb__rowlab', label);
      var helper = el('b'); helper.setAttribute('data-helper', kind + i);
      lab.appendChild(helper);
      var st = el('div', 'hh-qb__stepper');
      st.appendChild(btn('−', -1, i, kind, 'Fewer ' + label.toLowerCase() + ', ' + p.name));
      var box = el('input', 'hh-qb__qty');
      box.type = 'text'; box.inputMode = 'numeric';
      box.value = (kind === 'unit' && p.step_ft) ? '0 ft' : '0';
      box.setAttribute('data-qty', kind + i);
      box.setAttribute('aria-label', p.name + ' ' + label.toLowerCase());
      st.appendChild(box);
      st.appendChild(btn('+', 1, i, kind, 'More ' + label.toLowerCase() + ', ' + p.name, true));
      row.appendChild(lab); row.appendChild(st);
      return row;
    }
    function btn(txt, step, i, kind, aria, plus) {
      /* The class here is the BUTTON's, never the row's. An earlier version put
         'hh-qb__stepper' on the minus button, and that class is display:flex —
         so the button became a flex container and its glyph sat left-aligned
         while the plus button, which never got the class, stayed centred. */
      var b = el('button', 'hh-qb__btn' + (plus ? ' is-plus' : ''), txt);
      b.type = 'button';
      b.setAttribute('aria-label', aria);
      b.setAttribute('data-step', step);
      b.setAttribute('data-i', i);
      b.setAttribute('data-kind', kind);
      return b;
    }

    function setQty(i, kind, v) {
      var p = P[i], step = (kind === 'unit' && p.step_ft) ? p.step_ft : 1;
      var raw = parseInt(String(v).replace(/[^0-9]/g, ''), 10) || 0;
      var n = Math.max(0, Math.min(99999, step > 1 ? Math.round(raw / step) : raw));
      (kind === 'bin' ? bins : qty)[i] = n;
      root.querySelector('[data-qty="' + kind + i + '"]').value =
        step > 1 ? (n * step) + ' ft' : n;
      render();
    }
    wrap.addEventListener('click', function (e) {
      var b = e.target.closest('button[data-step]');
      if (!b) return;
      var i = +b.getAttribute('data-i'), kind = b.getAttribute('data-kind');
      var p = P[i], step = (kind === 'unit' && p.step_ft) ? p.step_ft : 1;
      var next = (kind === 'bin' ? bins : qty)[i] + (+b.getAttribute('data-step'));
      setQty(i, kind, step > 1 ? next * step : next);
    });
    wrap.addEventListener('change', function (e) {
      var cq = e.target.closest('[data-coq]');
      if (cq) { sel[+cq.getAttribute('data-coq')] = cq.checked; render(); return; }
      var f = e.target.closest('[data-qty]');
      if (!f) return;
      var key = f.getAttribute('data-qty');
      setQty(parseInt(key.replace(/^\D+/, ''), 10), key.replace(/\d+$/, ''), f.value);
    });

    /* ---- date needed (§1.1-1.4) ----------------------------------------- */
    /* ISO yyyy-mm-dd sorts lexicographically in date order, so the season guards
       compare as plain strings against the min/max the Liquid template wrote. */
    /* Pumpkins use a set-day SELECT, trees a free-form date input. Batch #11
       added a second mode alongside these; Batch #14 removed it. */
    /* Batch #14 §2 — pickup is gone. One fulfilment path, so there is no mode to
       read and nothing to switch between: pumpkins use the set-day select, trees
       the free-form date. fulfilMode() and paintFulfil() went with it. */
    function activeDateField() {
      return $('daysel') || $('date');
    }
    function dateVal()   { var f = activeDateField(); return f ? (f.value || '').trim() : ''; }
    function windowVal() {
      var f = $('window'); return f ? f.value : 'Either';
    }
    function dateState() {
      var f = activeDateField();
      if (!f) {
        /* No field at all. On trees that is the season guard: when the floor
           overtakes the season max the picker is deliberately absent and the
           text-us line shows instead, and submitting is still allowed, exactly
           as §B.2 established. Also the old harness case. */
        return 'ok';
      }
      var v = dateVal();
      if (!v) return 'empty';
      var lo = f.getAttribute('min') || '', hi = f.getAttribute('max') || '';
      if ((lo && v < lo) || (hi && v > hi)) return 'window';
      return 'ok';
    }
    /* Out of window, the text-us line is the ONLY thing shown — no rule, no day
       count, and nothing left over in the form-state line at the bottom. */
    /* Batch #13 §1 — named for what it is: the ORDER minimum, not a delivery
       minimum. (Batch #13 made one floor govern both fulfilment paths; Batch #14
       removed the second path, and the name still fits.) */
    var MIN_ORDER_LB = (D.minimum && D.minimum.type === 'weight') ? D.minimum.value : 2000;

    function paintDate() {
      var st = dateState(), f = activeDateField();
      var req = $('datereq'), win = $('datewin'), hid = $('datefield');
      var showWin = st === 'window';
      var showReq = st === 'empty' && dateTried;
      if (req) req.hidden = !showReq;
      if (win) win.hidden = !showWin;
      /* clear the marker off BOTH pumpkin controls, then set it on the live one,
         or a hidden field keeps a red ring it can never lose */
      [$('daysel'), $('date')].forEach(function (n) {
        if (n) n.classList.remove('is-missing');
      });
      if (f) f.classList.toggle('is-missing', showWin || showReq);
      if (hid) hid.value = dateVal();
    }

    /* §2.2 — every order is a delivery, so the ZIP is how the run gets planned
       and it is required. Same shape as the date: an inline message beside the
       field and a hard block at submit. Out-of-area is NOT a block — that buyer
       still gets a quote, they are simply not in the mileage table. The staff
       builder is exempt, as it is from the floor: DJ takes what he takes. */
    var zipTried = false;
    function zipState() {
      var f = $('zip');
      if (!f || mode === 'internal') return 'ok';
      /* Oct 26 fix: a pickup order has no delivery ZIP. With Delivery switched
         off the ZIP is not required (it was blocking every pickup submit). */
      var dz = $('del');
      if (dz && !dz.checked) return 'ok';
      return f.value.trim().length === 5 ? 'ok' : 'empty';
    }
    function paintZip() {
      var f = $('zip'), msg = $('zipreq');
      if (!f) return;
      var bad = zipState() !== 'ok' && zipTried;
      if (msg) msg.hidden = !bad;
      f.classList.toggle('is-missing', bad);
    }

    /* Batch #11 auto-switched a light order to pickup; Batch #13 removed the
       switch when one floor came to govern both paths, and Batch #14 removed the
       second path entirely. Nothing switches, nothing is disabled.

       What stays is the reading. setWeight() is still called from render() with
       the real running shipping weight and is still exposed on the root node,
       because the reading is what gates submit (§1.2). A null weight forces
       nothing — "no reading" is not "too light", and that is what keeps a
       freshly loaded builder silent. */
    var weightLb = null;
    function setWeight(lb) {
      weightLb = (lb === null || lb === undefined || isNaN(lb)) ? null : lb;
      paintMinimum();
    }

    /* §1.2 — one floor, both paths, both public builders. Each builder is
       measured against its OWN dataset minimum: pumpkins by shipping weight,
       trees by item count. The staff builder is exempt — DJ prices orders by
       hand and takes what he takes. An empty cart is never "under the
       minimum": that is the Batch #11 bug, and it stays fixed. */
    function underFloor(c) {
      if (mode === 'internal') return false;
      if (!c || !c.lines.length) return false;
      if (D.minimum && D.minimum.type === 'weight') {
        return weightLb !== null && weightLb < MIN_ORDER_LB;
      }
      return !c.meets;
    }

    /* §1.3 — the turn-away line. The copy is an editable theme setting rendered
       by the Liquid, and a blank setting renders no element at all, so this
       finds nothing to show and nothing empty is left on the page. The submit
       block in §1.2 does not depend on this element existing. */
    function paintMinimum() {
      var note = $('minnote');
      if (!note) return;
      note.hidden = !underFloor(compute());
    }
    function paintWindow() {
      var hid = $('winfield');
      if (hid) hid.value = windowVal();
    }

    /* ---- $1,200 tree minimum ------------------------------------------------
       Same arithmetic as priceAll() and the auto-invoice Apps Script: percent
       tiers per tier_group, money rounded once at the line, delivery = first
       20 mi flat then per mile. The staff builder has real prices and uses
       them. The public builder has NO prices: each product carries `mp`, its
       base price as a fraction of the minimum (see build_qb_data.py), so the
       dollar figure exists only in memory here and is never rendered. */
    function minDollars(lines, delivering, miles) {
      var V = D.minimum.value, priced = (mode === 'internal' && D.fees);
      var sub = 0;
      lines.forEach(function (l) {
        if (l.coq) return;
        var p = P[l.i], n = (p.unit === 'roll' || p.step_ft) ? l.ind : l.total, u;
        if (priced) u = unitPriceRaw(l.i);
        else {
          if (p.mp == null) return;
          /* mp x minimum recovers the whole-cent book price; snap it to the cent
             so the math is identical to the staff builder and the Apps Script. */
          var cur = tierOf(p.tier_group);
          var pct = (p.tier_group === 'trees' && !p.by_foot && retailMode()) ? 0
                  : ((cur && cur[1] != null) ? cur[1] : 0);
          u = priceBase(l.i) * (1 - pct);
        }
        /* unit to the cent, then extend — treePrice_() exactly */
        sub += Math.round(Math.round(u * 100) / 100 * n * 100) / 100;
      });
      var fee = 0;
      if (delivering) {
        if (priced) {
          var f = D.fees;
          fee = f.base_fee + Math.max(0, miles - f.incl_miles) * f.per_mile;
        } else {
          var md = D.mp_delivery || {};
          fee = Math.round(md.first * V * 100) / 100
              + Math.max(0, miles - md.incl_miles) * (Math.round(md.per_mile * V * 100) / 100);
        }
      }
      sub = Math.round(sub * 100) / 100 - treeExtra();
      return Math.round((sub + Math.round(fee * 100) / 100) * 100) / 100;
    }

    /* ---- compute -------------------------------------------------------- */
    function compute() {
      var items = 0, units = 0, netW = 0, shipW = 0, netBins = 0, lines = [], coq = [], nowt = [];
      /* bin_only SKUs carry no loose units, whatever is in qty[]. The stepper is
         not rendered for them, so this only fires if a value is injected or left
         over — but "the input isn't on screen" is not the same as "the number
         cannot be priced", and the money is what matters here. */
      P.forEach(function (p, i) { if (p.bin_only) qty[i] = 0; });
      P.forEach(function (p, i) {
        /* §2.2 — on the quote, but not in items, units, weight or the 2,000 lb
           threshold. A cart holding only these must read as NOT meeting it. */
        if (p.count_on_quote) {
          if (sel[i]) {
            coq.push(p.name);
            lines.push({i: i, name: p.name, cat: p.cat, group: p.tier_group,
                        ind: 0, bins: 0, total: 0, unit: p.unit,
                        roll_ft: 0, step_ft: 0, coq: true});
          }
          return;
        }
        var t = total(i);
        if (!t && !qty[i] && !bins[i]) return;
        items += qty[i] + bins[i];               // one roll / one bin = one item
        units += t;
        /* §D — coerce a missing weight to 0 for the SUM only. One SKU without a
           shipping weight must not turn the whole order total into NaN; it is
           reported separately below instead, so the omission is visible rather
           than silently absorbed. */
        if (p.wt_ship == null || (p.bin_orderable && p.wt_bin_ship == null)) nowt.push(p.name);
        netW += qty[i] * (p.wt || 0) + (p.bin_orderable ? bins[i] * (p.wt_bin_net || 0) : 0);
        shipW += qty[i] * (p.wt_ship || 0) + (p.bin_orderable ? bins[i] * (p.wt_bin_ship || 0) : 0);
        netBins += qty[i] * p.bpu + bins[i];
        lines.push({i: i, name: p.name, cat: p.cat, group: p.tier_group,
                    ind: qty[i], bins: bins[i], total: t, unit: p.unit,
                    roll_ft: p.roll_ft || 0, step_ft: p.step_ft || 0});
      });
      /* §C.2 — items that ARE in the totals (real count and weight) but carry
         no per-unit price yet. Distinct from coq: these do count toward the
         2,000 lb minimum, they simply contribute $0 to the money total. */
      var nopr = lines.filter(function (l) {
        return !l.coq && P[l.i].base == null && l.total > 0;
      }).map(function (l) { return P[l.i].name; });
      /* FIX 15 Sep: the box-truck test counts PUMPKIN bins only. Hay is sourced
         locally, never rides the farm truck, and counting it billed a $450 truck
         to buyers whose own quote said they did not need one. */
      var pkBins = P.reduce(function (a, p, i) {
        return a + (p.cat === 'Hay' ? 0 : qty[i] * p.bpu + bins[i]); }, 0);
      var zip = $('zip').value.trim();
      var miles = D.zips[zip], zipOk = miles !== undefined;
      var delOn = $('del').checked;
      var delivering = delOn && zipOk && (units > 0 || coq.length > 0);
      var m = D.minimum, met;
      /* FIX 15 Sep: was lb(shipW). Shipping weight includes pallet and crate,
         so a load 20% under the real minimum reported MET. Nine of twenty-five
         submissions were waved through this way. Net weight is the honest test. */
      var minAmt = null;
      if (m.type === 'weight') met = lb(netW) >= m.value;
      else if (m.type === 'dollars') {
        /* Oct 22, 2026 — trees: $1,200 of product after volume tiers, plus
           delivery at book rate when delivering. Tax, install and box truck do
           not count. Half-a-cent tolerance only, so float noise cannot lock out
           an order the auto-invoice script prices at exactly $1,200.00. */
        minAmt = minDollars(lines, delivering, miles);
        met = lines.length > 0 && minAmt >= m.value - 0.005;
      }
      else met = items >= m.value;
      return {items: items, units: units, net: netW, ship: shipW, bins: netBins, nopr: nopr, nowt: nowt,
              lines: lines, coq: coq, zip: zip, miles: miles, zipOk: zipOk, delOn: delOn,
              delivering: delivering, install: $('inst').checked,
              pkBins: pkBins,
              /* FIX 15 Sep: was `delivering && ...`, and `delivering` requires a ZIP
                 in our mileage table - so every out-of-area buyer was told NO box
                 truck however many bins they ordered. Seven orders, the largest of
                 them nine bins. The truck a load needs does not depend on the ZIP. */
              boxTruck: delOn && pkBins > D.bin_limit, meets: met, minAmt: minAmt};
    }

    /* ---- render --------------------------------------------------------- */
    function render() {
      var c = compute();
      /* §2.4, kept by Batch #13 §1.1 — this is the call that makes the weight
         rule real. Without it the reading is inert: the rule only ever fires
         from a recalculation.
         An EMPTY order passes null, not 0. Zero is a reading; nothing in the
         cart is the absence of one, and a null forces nothing. Passing 0 here
         made a freshly loaded builder announce it was under the minimum before
         the buyer had added a single pumpkin. */
      setWeight(c.lines.length ? lb(c.ship) : null);

      var zs = $('zipstate');
      zs.className = 'hh-qb__state';
      if (!c.delOn) zs.textContent = TREEPUB ? 'Pickup — Dripping Springs, TX.' : 'Pickup quote — Dripping Springs, TX.';
      else if (c.zip.length < 5) zs.textContent = 'Enter your zip for a delivered quote.';
      else if (c.zipOk) { zs.className = 'hh-qb__state is-ok';
        zs.textContent = TREEPUB ? '✓ Delivery available to ' + c.zip
                                 : '✓ Delivery available to ' + c.zip + ' — included in your quote'; }
      else { zs.className = 'hh-qb__state is-bad';
        /* §2 — this read "pickup quote", which is no longer an option. Reworded
           out of necessity; flagged in the gate report for Taylor's sweep. */
        zs.textContent = TREEPUB ? 'Outside our usual delivery area — DJ will email you to confirm'
                                 : 'Outside our usual delivery area — we will confirm before quoting'; }

      /* per-section meter + note */
      var seen = {};
      P.forEach(function (p) {
        if (seen[p.cat]) return;
        seen[p.cat] = 1;
        var head = catNodes[p.cat];
        var groups = {};
        P.forEach(function (q) { if (q.cat === p.cat) groups[q.tier_group] = 1; });
        var parts = [], pct = 0;
        Object.keys(groups).forEach(function (g) {
          if (g === 'trees' && TP) {
            var a25 = treeAt25(), rm = retailMode();
            pct = Math.max(pct, Math.min(100, (a25 / TP.big_over) * 100));
            if (rm && a25 > TP.big_over) parts.push('25% wholesale pricing applied ✓ · extra 5% off trees over $2,000 applied ✓');
            else if (rm) parts.push('25% wholesale pricing applied ✓ · Extra 5% off trees on every dollar over $2,000.');
            else parts.push('Wholesale trees are 25% off · Extra 5% off trees on every dollar over $2,000.');
            return;
          }
          var cur = tierOf(g), nx = nextTier(g), n = groupCount(g);
          var noun = nounFor(g, p.cat);
          var lad = LADDER[g] || [];
          var top = lad.length ? lad[lad.length - 1][0] : 1;
          pct = Math.max(pct, Math.min(100, (n / top) * 100));
          if (MODEL === 'break') {
            if (!lad.length) parts.push('');
            else if (cur) parts.push('Volume pricing applied ✓');
            else parts.push('Add ' + (nx[0] - n) + ' more ' + noun + ' to unlock volume pricing');
          }
          else if (!cur && nx) parts.push('Add ' + (nx[0] - n) + ' more ' + noun + ' to save ' + Math.round(nx[1] * 100) + '%');
          else if (cur && nx) parts.push(Math.round(cur[1] * 100) + '% volume savings applied · add ' + (nx[0] - n) + ' more to save ' + Math.round(nx[1] * 100) + '%');
          else if (cur) parts.push(Math.round(cur[1] * 100) + '% volume savings applied');
          else parts.push('Volume savings at ' + (lad[0] ? lad[0][0] : '') + '+ ' + noun);
        });
        var meter = head.querySelector('.hh-qb__meter');
        meter.querySelector('i').style.width = pct + '%';
        meter.classList.toggle('is-max', pct >= 100);
        var note = head.querySelector('.hh-qb__cat-note');
        note.textContent = parts.join(' · ');
        note.classList.toggle('is-on', /applied/.test(note.textContent));
      });

      /* per-card */
      P.forEach(function (p, i) {
        var card = root.querySelector('.hh-qb__prod[data-i="' + i + '"]');
        card.classList.toggle('is-active', p.count_on_quote ? sel[i] : total(i) > 0);
        var cur = tierOf(p.tier_group), nx = nextTier(p.tier_group), n = groupCount(p.tier_group);
        var noun = nounFor(p.tier_group, p.cat);
        var t = root.querySelector('[data-tier="' + i + '"]');
        if (p.count_on_quote) {
          /* no volume line: an item with no price cannot unlock volume pricing */
          t.textContent = '';
        } else if (p.tier_group === 'trees' && TP) {
          t.textContent = !p.by_foot ? ''
            : (retailMode() ? Math.round(cur[1] * 100) + '% wholesale pricing applied' : '25% off wholesale');
        } else if (p.tier_group === 'garland') {
          var unitTxt = function (steps) {
            return p.step_ft ? (steps * p.step_ft) + ' ft more'
                             : steps + ' more roll' + (steps === 1 ? '' : 's');
          };
          if (!cur && nx) t.textContent = 'Add ' + unitTxt(nx[0] - n) + ' to save ' + Math.round(nx[1] * 100) + '%';
          else if (cur && nx) t.textContent = Math.round(cur[1] * 100) + '% applied · add ' + unitTxt(nx[0] - n) + ' to save ' + Math.round(nx[1] * 100) + '%';
          else if (cur) t.textContent = Math.round(cur[1] * 100) + '% applied';
        } else if (MODEL === 'break') {
          var lad2 = LADDER[p.tier_group] || [];
          t.textContent = !lad2.length ? ''
            : cur ? 'Volume pricing applied ✓'
                  : lad2[0][0] + '+ unlocks volume pricing';
        } else {
          if (!cur && nx) t.textContent = nx[0] + '+ ' + noun + ' saves ' + Math.round(nx[1] * 100) + '%';
          else if (cur && nx) t.textContent = Math.round(cur[1] * 100) + '% applied · ' + nx[0] + '+ saves ' + Math.round(nx[1] * 100) + '%';
          else if (cur) t.textContent = Math.round(cur[1] * 100) + '% applied';
        }
        t.classList.toggle('is-on', /applied/.test(t.textContent));

        var uh = root.querySelector('[data-helper="unit' + i + '"]');
        if (uh) {
          if (p.unit === 'roll' && qty[i])            /* internal only */
            uh.textContent = (qty[i] * p.roll_ft) + ' ft · ' + qty[i] + ' roll' + (qty[i] === 1 ? '' : 's');
          else uh.textContent = '';
        }
        var bh = root.querySelector('[data-helper="bin' + i + '"]');
        if (bh) bh.textContent = bins[i]
          ? bins[i] + ' bin' + (bins[i] === 1 ? '' : 's') + ' ≈ ' + (bins[i] * p.per_bin).toLocaleString('en-US') + ' pumpkins' : '';

        if (mode === 'internal') {
          var pn = root.querySelector('[data-price="' + i + '"]');
          /* Batch #10B §C.2 — two states reach this line. count_on_quote has
             neither a count nor a price; Porcelain Doll and Choga now have a
             real 78/bin count but still no approved per-unit price, so base is
             null. Both must read as unpriced: unitPrice() returns 0 for a null
             base, and rendering "$0.00 / unit" on a staff quote is how an item
             gets sent out free. */
          if (p.count_on_quote || p.base == null) {
            pn.textContent = 'price on quote — DJ to set'; return;
          }
          var unit = unitPrice(i);
          pn.textContent = money(unit) + ' / ' + (p.unit === 'roll' ? 'roll' : (p.step_ft ? 'roll' : 'unit'))
            + (total(i) ? '  ·  line ' + money(unit * (p.unit === 'roll' ? qty[i] : total(i))) : '');
        }
      });

      /* minimum */
      var m = D.minimum, mn = $('min');
      mn.setAttribute('data-state', c.meets ? 'met' : 'below');
      if (m.type === 'weight') {
        mn.textContent = c.meets
          ? "That's a ton ✓ — " + m.value.toLocaleString('en-US') + ' lb minimum met'
          : 'About ' + fmtLb(c.ship) + ' so far — add more to reach the one-ton minimum';
      } else if (m.type === 'dollars') {
        /* Oct 22 — progress bar. The ONLY dollar figure is the minimum itself:
           no running total, no "$X to go". The nudge may suggest an item count. */
        mn.textContent = '';
        var pct = c.lines.length ? Math.max(0, Math.min(100, (c.minAmt || 0) / m.value * 100)) : 0;
        mn.appendChild(el('span', 'hh-qb__minlabel', '$' + m.value.toLocaleString('en-US') + ' wholesale minimum'));
        var bar = el('span', 'hh-qb__minbar');
        bar.setAttribute('role', 'progressbar');
        bar.setAttribute('aria-label', 'Progress toward the wholesale minimum');
        bar.setAttribute('aria-valuemin', '0'); bar.setAttribute('aria-valuemax', '100');
        bar.setAttribute('aria-valuenow', String(Math.round(pct)));
        var fill = el('i'); fill.style.width = pct + '%'; bar.appendChild(fill);
        bar.classList.toggle('is-max', c.meets);
        mn.appendChild(bar);
        var nudge = '';
        if (c.lines.length && c.meets) nudge = "You've hit the wholesale minimum ✓";
        else if (c.lines.length) {
          var ri = -1;
          P.forEach(function (q, j) { if (ri < 0 && /^Noble Fir \(6/.test(q.name)) ri = j; });
          var rp = ri >= 0 ? P[ri] : null, rv = 0;
          if (rp) {
            if (mode === 'internal' && D.fees) rv = unitPriceRaw(ri);
            else if (rp.mp != null) {
              var rc = tierOf(rp.tier_group) || (rp.by_foot && TP ? [0, TP.off] : null);
              rv = (Math.round(rp.mp * m.value * 100) / 100) * ((rc && rc[1] != null) ? (1 - rc[1]) : 1);
            }
          }
          var need = rv > 0 ? Math.ceil((m.value - (c.minAmt || 0)) / rv) : 0;
          nudge = "You're almost at wholesale. "
                + (need > 0 && need <= 6
                   ? 'Add about ' + need + ' more 6–7 ft tree' + (need === 1 ? '' : 's')
                     + ', or a few wreaths or some garland, to qualify.'
                   : 'Add a few more trees, wreaths or garland to qualify.');
        }
        mn.appendChild(el('span', 'hh-qb__minnudge', nudge));
        /* the lock: the button stays disabled until the order qualifies. The
           submit handler re-checks (underFloor), so re-enabling the button in
           devtools still sends nothing. Staff builder is exempt. */
        if (mode !== 'internal' && !sent) {
          var sb = $('send');
          if (sb) {
            sb.disabled = !c.meets;
            sb.setAttribute('aria-disabled', c.meets ? 'false' : 'true');
          }
        }
      } else {
        mn.textContent = c.meets
          ? m.value + '-item minimum met ✓'
          : 'Add ' + (m.value - c.items) + ' more to reach the ' + m.value + '-item minimum';
      }

      /* order sheet — counts and weight, never money (public) */
      var sheet = $('sheet');
      sheet.textContent = '';
      if (!c.lines.length) {
        sheet.appendChild(el('p', 'hh-qb__sheet-empty', 'Nothing added yet — use the + buttons above.'));
      } else {
        c.lines.forEach(function (l) {
          var right = l.coq ? 'count on quote'
            : l.step_ft
            ? (l.ind * l.step_ft) + ' ft'                                  /* public: feet only */
            : l.unit === 'roll'
              ? (l.ind * l.roll_ft) + ' ft · ' + l.ind + ' roll' + (l.ind === 1 ? '' : 's')
              : (l.bins ? l.ind + ' + ' + l.bins + ' bin' + (l.bins === 1 ? '' : 's') + ' ≈ ' + l.total : String(l.total));
          sheet.appendChild(line(l.name, right));
        });
        if (c.install) sheet.appendChild(svc('On-site installation'));
        if (c.boxTruck) sheet.appendChild(svc('Large load — box truck'));
        sheet.appendChild(line('Total items', c.items + (c.items === 1 ? ' item' : ' items')));
        sheet.appendChild(line('Est. shipping weight', fmtLb(c.ship)));
        if (mode === 'internal') {
          var t = el('div', 'hh-qb__total', 'Grand total ' + money(priceAll(c).total));
          sheet.appendChild(t);
        }
      }
      function line(a, b) {
        var d = el('div', 'hh-qb__sline');
        d.appendChild(el('span', null, a)); d.appendChild(el('b', null, b));
        return d;
      }
      function svc(t) { var d = line(t, 'included'); d.className = 'hh-qb__sline is-svc'; return d; }
    }

    /* ---- internal pricing (never reachable in public mode) ------------- */
    /* 2026-09-10 — split into RAW and DISPLAY.

       Every price used to be rounded to cents here, before anything multiplied
       by it. That is fine while unit prices are whole cents, and wrong the
       moment one is not: Decorative Mix is a flat $650 bin of 45, so its
       per-unit figure is 14.444444, and rounding first made the bin
       45 x 14.44 = $649.80 — twenty cents light on every mixed bin sold.

       So money is now rounded ONCE, at the line, the way an invoice does it:
       45 x 14.444444 = 649.99998 -> $650.00 exactly. unitPrice() still rounds,
       because that is the number shown beside the row, and a buyer should not
       be shown six decimals. */
    function unitPriceRaw(i) {
      var p = P[i];
      if (p.base == null) return 0;
      if (p.tier_group === 'trees' && !p.by_foot && retailMode()) return p.base;
      var cur = tierOf(p.tier_group);
      if (!cur) return p.base;
      /* break model carries the volume price per product; percent model derives it */
      if (cur[1] == null) return (p.vol != null ? p.vol : p.base);
      return p.base * (1 - cur[1]);
    }
    function unitPrice(i) {          /* display only */
      return Math.round(unitPriceRaw(i) * 100) / 100;
    }
    function lineTotal(i, n) {       /* money — rounded once, here */
      /* trees dataset: the Apps Script rounds the unit to the cent first, then
         extends — match it exactly or a 75%-of-$35.25 line drifts a cent */
      if (D.label === 'trees') return Math.round(Math.round(unitPriceRaw(i) * 100) / 100 * n * 100) / 100;
      return Math.round(unitPriceRaw(i) * n * 100) / 100;
    }
    function priceAll(c) {
      var sub = 0;
      c.lines.forEach(function (l) {
        var p = P[l.i];
        sub += lineTotal(l.i, (p.unit === 'roll' || p.step_ft) ? l.ind : l.total);
      });
      var f = D.fees || {};
      var fee = c.delivering ? (f.base_fee + Math.max(0, c.miles - f.incl_miles) * f.per_mile) : 0;
      var inst = c.install ? f.install : 0;
      var truck = c.boxTruck ? f.box_truck : 0;
      var extra = treeExtra();
      sub = Math.round((Math.round(sub * 100) / 100 - extra) * 100) / 100;
      return {sub: sub, extra: extra, fee: fee, install: inst, truck: truck,
              total: Math.round((sub + fee + inst + truck) * 100) / 100};
    }

    /* ---- the raw order that goes to DJ --------------------------------- */
    /* ── Batch #12 §2 — quote_id ──────────────────────────────────────────
       Generated AT SUBMIT, never at page load. Two submissions from the same
       buyer are two quotes and must get two IDs; the ledger deduplicates by
       reading them, not by us refusing to issue one. Generating at load would
       also mean a buyer who sits on the page for an hour submits a stale ID.

       Q26-MMDD-XXXX. Short enough to read down a phone, and the alphabet drops
       every character that is ambiguous out loud or in handwriting: no O or 0,
       no I or 1. That leaves 32 symbols, so the 4-character suffix carries
       32^4 = 1,048,576 combinations per day — collisions are not a practical
       concern at H&H's volume, and there is no shared state between sessions to
       run a counter against anyway.

       crypto.getRandomValues where available; Math.random is a fallback, not
       the primary source, because some browsers seed it poorly. */
    var QID_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';   /* no O 0 I 1 */
    function makeQuoteId() {
      var d = new Date();
      var mmdd = ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2);
      var yy = String(d.getFullYear()).slice(-2);
      var n = 4, out = '', i;
      var buf = null;
      try {
        if (window.crypto && window.crypto.getRandomValues) {
          buf = new Uint32Array(n);
          window.crypto.getRandomValues(buf);
        }
      } catch (e) { buf = null; }
      for (i = 0; i < n; i++) {
        var r = buf ? buf[i] : Math.floor(Math.random() * 4294967296);
        out += QID_ALPHABET.charAt(r % QID_ALPHABET.length);
      }
      return 'Q' + yy + '-' + mmdd + '-' + out;
    }
    var quoteId = null;          /* set at submit, read by orderText + payload */

    function orderText(c) {
      var label = D.label === 'trees' ? 'tree' : 'pumpkin';
      var out = 'HOLIDAY & HARVEST — wholesale ' + label + ' quote request\n\n';
      /* §2.2 — first line of the body, own line, fixed prefix so the ledger
         parser can grab it with one anchored match instead of guessing. */
      out += 'QUOTE ID:         ' + (quoteId || '(not generated)') + '\n';
      out += 'SOURCE:           ' + (mode === 'internal' ? 'STAFF (priced live by DJ)'
                                                         : 'PUBLIC (buyer self-serve)') + '\n';
      /* §1.4 — date and window at the TOP of the body, above the raw order. */
      out += 'FULFILMENT:       DELIVERY — Central Texas\n';
      out += 'DATE NEEDED:      ' + (prettyDate(dateVal()) || '(none given)') + '\n';
      out += 'PREFERRED WINDOW: ' + windowVal() + '\n\n';
      out += 'Name: ' + $('name').value.trim() + '\n';
      if ($('co').value.trim()) out += 'Company: ' + $('co').value.trim() + '\n';
      out += 'Email: ' + $('email-field').value.trim() + '\n';
      if ($('phone').value.trim()) out += 'Phone: ' + $('phone').value.trim() + '\n';
      out += '\nOrder\n';
      c.lines.forEach(function (l) {
        var p = P[l.i];
        if (l.coq) { out += '  count on quote × ' + l.name + '   [NEEDS MANUAL PRICING]\n'; return; }
        if (p.step_ft || p.unit === 'roll') {
          var ft = l.ind * (p.step_ft || p.roll_ft);
          out += '  ' + ft + ' ft · ' + l.ind + ' roll' + (l.ind === 1 ? '' : 's') + ' × ' + l.name + '\n';
        }
        else if (l.bins) out += '  ' + l.bins + ' bin' + (l.bins === 1 ? '' : 's') + ' + ' + l.ind + ' individual' + (l.ind === 1 ? '' : 's') + ' × ' + l.name + ' ≈ ' + l.total + ' units\n';
        else out += '  ' + l.total + ' × ' + l.name + '\n';
      });
      if (c.coq.length) {
        out += '\n  ^ ' + c.coq.join(' and ') + ' have no per-unit count or weight yet —\n'
             + '    price and quantity these by hand. They are excluded from every\n'
             + '    total below, including the 2,000 lb minimum.\n';
      }
      if (c.nowt && c.nowt.length) {
        out += '\n  ^ ' + c.nowt.join(' and ') + ' have no shipping weight on file —\n'
             + '    they are counted as 0 lb in the totals below, so the 2,000 lb\n'
             + '    minimum may read low. Weigh these before quoting.\n';
      }
      /* 2026-09-10 — STAFF ONLY, and it always should have been.

         nopr is `P[i].base == null`, and the PUBLIC dataset carries no `base`
         key at all — money never reaches it, by design. `undefined == null` is
         true in JS, so on the public builder this fired for EVERY line of every
         order, telling DJ that correctly-priced items had "NO per-unit price
         yet" and contributed $0. It is the staff builder that can genuinely have
         a null price, and the public email already ends with "Price this order
         in the staff builder", so on public the block was both wrong and
         redundant. Found while verifying Decorative Mix, which it accused of
         being unpriced. */
      if (mode === 'internal' && c.nopr && c.nopr.length) {
        out += '\n  ^ ' + c.nopr.join(' and ') + ' have a real count and weight but\n'
             + '    NO per-unit price yet — they are in the weight totals and the\n'
             + '    2,000 lb minimum, but contribute $0 to the money total. Price\n'
             + '    these by hand before sending.\n';
      }
      out += '\nTier reached by section\n';
      var done = {};
      P.forEach(function (p) {
        if (done[p.tier_group]) return;
        done[p.tier_group] = 1;
        var n = groupCount(p.tier_group);
        if (!n) return;
        var cur = tierOf(p.tier_group);
        out += '  ' + p.cat + (p.tier_group === 'garland' ? ' (garland)' : '') + ': ' + n
             + ' — ' + (!cur ? 'base tier'
                        : cur[1] == null ? 'volume tier' : Math.round(cur[1] * 100) + '% tier') + '\n';
      });
      out += '\nTotals\n';
      out += '  Items (minimum counts these): ' + c.items + '\n';
      out += '  Units: ' + c.units + '\n';
      out += '  Est. product weight (net): ' + fmtLb(c.net) + '\n';
      out += '  Est. shipping weight (what the buyer saw): ' + fmtLb(c.ship) + '\n';
      out += '  Minimum ' + (D.minimum.type === 'weight' ? D.minimum.value + ' lb'
              : D.minimum.type === 'dollars' ? '$' + D.minimum.value.toLocaleString('en-US')
              : D.minimum.value + ' items') + ': ' + (c.meets ? 'MET' : 'NOT met') + '\n';
      out += '\nDelivery & services\n';
      /* §3 — every order is a delivery; the ZIP always prints. Out-of-area
         still prints the ZIP, marked, rather than dropping it. */
      if (c.delivering) out += '  Delivery to ' + c.zip + ' (' + c.miles + ' mi)\n';
      else if (c.zip) out += '  Delivery ZIP: ' + c.zip
                          + (c.zipOk ? ' (' + c.miles + ' mi)' : ' (not in our mileage table)') + '\n';
      else out += '  Delivery ZIP: (none given)\n';
      out += '  On-site installation: ' + (c.install ? 'REQUESTED' : 'no') + '\n';
      out += '  Box truck required: ' + (c.boxTruck ? 'YES' : 'no') + '\n';
      if ($('notes').value.trim()) out += '\nNotes: ' + $('notes').value.trim() + '\n';
      out += '\nPrice this order in the staff builder.\n';
      return out;
    }

    /* ---- submit --------------------------------------------------------- */
    function fail(msg) {
      var fs = $('formstate'); fs.className = 'hh-qb__state is-bad'; fs.textContent = msg;
    }
    function clearState() {
      var fs = $('formstate'); fs.className = 'hh-qb__state'; fs.textContent = '';
    }
    function onSuccess() {
      if (sent) return;
      sent = true;
      try {
        if (typeof window.hhTrack === 'function') window.hhTrack('wholesale_form_submit');
        else (window.dataLayer = window.dataLayer || []).push({event: 'wholesale_form_submit'});
        if (typeof window.hhAction === 'function') window.hhAction('quote_submit', ctx);
        else (window.dataLayer = window.dataLayer || []).push({
          event: 'hh_action', hh_action: 'quote_submit', hh_context: ctx,
          hh_page: window.location.pathname});
      } catch (e) {}
      var b = $('send'); b.disabled = true; b.textContent = TREEPUB ? 'Order sent ✓' : 'Request sent ✓';
      var fs = $('formstate'); fs.className = 'hh-qb__state is-ok';
      /* §2.3 — the buyer leaves with a key, not a description of their order. */
      if (TREEPUB) {
        /* Oct 22 — trees auto-invoice. No quote is coming, so none is promised. */
        fs.textContent = "Thanks. Your invoice is on its way by email within about 15 minutes. "
                       + "It's for a 75% deposit; the rest is due at pickup or delivery. "
                       + "If you asked for installation or delivery outside our area, DJ will "
                       + "email you personally to confirm."
                       + (quoteId ? '  Your order reference: ' + quoteId : '');
      } else {
        fs.textContent = "Request sent ✓ — we'll reply with your quote and deposit "
                       + "invoice within one business day."
                       + (quoteId ? '  Your quote reference: ' + quoteId : '');
      }
    }

    root.querySelector('form').addEventListener('submit', function (e) {
      e.preventDefault();
      if (sent) return;
      var c = compute();
      if (!c.lines.length) { fail(TREEPUB ? 'Add at least one item.' : 'Add at least one item to request a quote.'); return; }
      /* Batch #13 §1.2 — the floor blocks the submit, in BOTH fulfilment modes.
         Checked before the date because a buyer who is under the minimum has a
         different problem from one who picked a bad day, and telling them about
         the day first wastes the trip. Inline message, no dialog, and the
         form-state line is cleared so only one message is on screen — the same
         shape as the date validation below. */
      if (underFloor(c)) {
        paintMinimum();
        clearState();
        var mnote = $('minnote') || $('min');
        if (mnote) mnote.scrollIntoView({behavior: 'smooth', block: 'center'});
        return;
      }
      /* §1.1 — the date blocks the submit. Its message is inline, beside the
         field; the form-state line stays empty so only one message is on screen. */
      if (dateState() !== 'ok') {
        dateTried = true;
        paintDate();
        clearState();
        var df = $('date');
        if (df) { try { df.focus({preventScroll: true}); } catch (e2) { df.focus(); } }
        (root.querySelector('.hh-qb__when') || df).scrollIntoView({behavior: 'smooth', block: 'center'});
        return;
      }
      /* §2.2 — the ZIP blocks the submit now that every order is a delivery.
         Checked after the date so the buyer fixes one thing at a time, in the
         order the fields appear. */
      if (zipState() !== 'ok') {
        zipTried = true;
        paintZip();
        clearState();
        var zfld = $('zip');
        if (zfld) {
          try { zfld.focus({preventScroll: true}); } catch (e3) { zfld.focus(); }
          zfld.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
        return;
      }
      var ok = true;
      /* keys map to [data-hh-qb-<key>]; the email input is data-hh-qb-email-field
         because data-hh-qb-email is the wrapper's mailto fallback address */
      ['name', 'email-field'].forEach(function (k) {
        var f = $(k);
        var miss = !f || !f.value.trim();
        if (f) f.classList.toggle('is-missing', miss);
        if (miss) ok = false;
      });
      if (!ok) {
        fail(TREEPUB ? 'Name and email are required so we can send your invoice.'
                     : 'Name and email are required so we can send your quote.');
        $('contact').scrollIntoView({behavior: 'smooth', block: 'center'});
        return;
      }
      /* §2.1 — generated HERE, once per submission, before the body is built. */
      quoteId = incomingQuote || makeQuoteId();
      var qf = $('qidfield'); if (qf) qf.value = quoteId;
      var qs = $('qsrcfield'); if (qs) qs.value = (mode === 'internal' ? 'staff' : 'public');
      var subject = 'Wholesale ' + (D.label === 'trees' ? 'tree' : 'pumpkin')
                  + ' quote request — ' + quoteId + ' — '
                  + ($('co').value.trim() || $('name').value.trim());
      var body = orderText(c);
      var b = $('send'); b.disabled = true; b.textContent = 'Sending…';
      $('formstate').className = 'hh-qb__state'; $('formstate').textContent = '';
      fetch(endpoint, {
        method: 'POST',
        headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
        body: JSON.stringify({
          quote_id: quoteId,
          /* §2.2 — a separate field, not encoded into the ID: the ledger needs
             to filter on origin without parsing, and an ID that means two things
             is an ID that eventually gets parsed wrong. */
          quote_source: mode === 'internal' ? 'staff' : 'public',
          /* §2.2 — CONSTANT "delivery" from Batch #14 onward. Deliberately NOT
             removed: the ledger importer may branch on it, and a field that
             disappears breaks a parser while a field that goes constant does not. */
          /* Oct 26 fix: was the constant "delivery" since Batch #14 (pumpkins had
             no pickup). The tree builder has a pickup switch and the auto-invoicer
             reads this field, so it now says what the buyer chose. Builders with
             no switch (pumpkins) still send "delivery". */
          fulfilment_type: ($('del') && !$('del').checked) ? 'pickup' : 'delivery',
          date_needed: dateVal(), delivery_window: windowVal(),
          manual_pricing_needed: c.coq.join(', '),
          name: $('name').value.trim(), company: $('co').value.trim(),
          email: $('email-field').value.trim(), phone: $('phone').value.trim(),
          notes: $('notes').value.trim(), dataset: D.label, order: body,
          items: String(c.items), units: String(c.units),
          net_weight_lb: String(lb(c.net)), shipping_weight_lb_buyer_saw: String(lb(c.ship)),
          minimum_met: c.meets ? 'yes' : 'no',
          /* Batch #12 §3 — UNCONDITIONAL, and it stays that way. This used to
             read `c.delivering ? c.zip : ''`, and `delivering` is
             `delOn && zipOk && items`, so the ZIP was destroyed on three paths:
             the then-existing pickup mode, any buyer outside the service area,
             and any empty cart. Batch #14 makes the ZIP required, so the first
             path is gone — but the other two remain, and the out-of-area case is
             exactly the one where the ledger still needs the ZIP. Miles is
             derived rather than entered, so it is sent when the ZIP is in the
             map and empty when it is not: empty means "not in our table", never
             "no ZIP given". */
          delivery_zip: c.zip,
          delivery_miles: c.zipOk ? String(c.miles) : '',
          delivery_zip_in_area: c.zipOk ? 'yes' : (c.zip ? 'no' : ''),
          install_requested: c.install ? 'yes' : 'no',
          box_truck_required: c.boxTruck ? 'yes' : 'no',
          _subject: subject, _replyto: $('email-field').value.trim()
        })
      }).then(function (r) {
        if (!r.ok) throw new Error('bad status');
        onSuccess();
      }).catch(function () {
        b.disabled = false; b.textContent = TREEPUB ? 'Send my order' : 'Send quote request';
        var fs = $('formstate'); fs.className = 'hh-qb__state is-ok';
        fs.textContent = 'Opening your email app…';
        window.location.href = 'mailto:' + fallbackEmail + '?subject='
          + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
      });
    });

    var toggle = $('toggle'), sheet = $('sheet');
    toggle.addEventListener('click', function () {
      var open = sheet.hasAttribute('hidden');
      if (open) sheet.removeAttribute('hidden'); else sheet.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = open ? 'Hide your order' : 'View your order';
    });
    ['zip', 'del', 'inst'].forEach(function (k) {
      $(k).addEventListener('input', render);
      $(k).addEventListener('change', render);
    });
    /* §1.1/§1.2 — keep the two hidden fields in step with the visible controls,
       so a native submit carries the same pair of names as the JSON payload. */
    var wfld = $('window');
    [$('date'), $('daysel')].forEach(function (n) {
      if (!n) return;
      ['input', 'change', 'blur'].forEach(function (ev) { n.addEventListener(ev, paintDate); });
    });
    if (wfld) wfld.addEventListener('change', paintWindow);
    var zf = $('zip');
    if (zf) ['input', 'change', 'blur'].forEach(function (ev) { zf.addEventListener(ev, paintZip); });
    paintDate();
    paintWindow();
    root.hhSetWeight = setWeight;            /* §2.4 — exposed for direct driving */

    if (mode === 'internal') {
      var cp = $('copy');
      if (cp) cp.addEventListener('click', function () {
        var c = compute(), pr = priceAll(c), t = orderText(c) + '\nPRICED\n';
        c.lines.forEach(function (l) {
          if (l.coq) { t += '  count on quote × ' + l.name + ' @ PRICE TBD\n'; return; }
          var p = P[l.i], u = unitPrice(l.i), n = p.unit === 'roll' ? l.ind : l.total;
          t += '  ' + n + ' × ' + l.name + ' @ ' + money(u) + ' = ' + money(lineTotal(l.i, n)) + '\n';
        });
        if (pr.extra) t += '  Extra 5% off trees over $2,000  −' + money(pr.extra) + '\n';
        t += '  Subtotal ' + money(pr.sub) + '\n  Delivery ' + money(pr.fee)
           + '\n  Install ' + money(pr.install) + '\n  Box truck ' + money(pr.truck)
           + '\n  GRAND TOTAL ' + money(pr.total) + '\n';
        navigator.clipboard.writeText(t).then(function () {
          cp.textContent = 'Copied ✓';
          setTimeout(function () { cp.textContent = 'Copy quote'; }, 1600);
        });
      });
    }

    if (incomingQuote) {
      var rb = el('div', 'hh-qb__resume');
      rb.setAttribute('role', 'status');
      rb.style.cssText = 'margin:0 0 18px;padding:12px 16px;border-left:3px solid #1C5138;'
        + 'background:#E3EBE4;color:#161D18;border-radius:0 6px 6px 0;font-size:14px;'
        + 'line-height:1.5';
      rb.appendChild(el('b', null, 'Editing your existing request '
        + incomingQuote.replace(/[^A-Za-z0-9-]/g, '') + '.'));
      rb.appendChild(document.createTextNode(' '
        + (prefilled ? 'Your order is loaded below \u2014 change anything you like. ' : '')
        + 'When you send it, this replaces the original rather than adding a second one.'));
      root.insertBefore(rb, root.firstChild);
    }

    root.setAttribute('data-hh-qb-loaded', '1');
    render();
  }

  function boot() {
    var n = document.querySelectorAll('[data-hh-qb]');
    for (var i = 0; i < n.length; i++) init(n[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.hhQbBoot = boot;
})();
